// ** React Imports
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

//** Type Imports */
import {
  CatalogueApplicationType,
  CatalogueUIConfigType,
  DiscountFormErrorState,
  DiscountFormStateType,
  QuantityDiscountorBlockConfigType
} from 'src/lib/types/discounts'
import { CatalogueCategoryType, CatalogueDataType } from 'src/lib/types/common/catalogue'

// ** Child Imports
import { XCircle } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'

// Util Imports
import produce from 'immer'
import { disablePlusMinusEKeys, getCatalogueIds, getValueInRange } from 'src/lib/utils/helpers'
import SearchAsYouType from 'src/components/common/catalogue-search-component/subcomponents/SearchAsYouType'
import CatalogueSearch from 'src/components/common/catalogue-search-component/CatalogueSearch'
import SelectedItemsList from 'src/components/common/catalogue-search-component/subcomponents/SelectedItemsList'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'

interface QuantityDistributionBlockProps {
  blocksData: QuantityDiscountorBlockConfigType[] | undefined
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  catalogueApplicationType: CatalogueApplicationType
  catalogueCatagory: CatalogueCategoryType
  blockData: QuantityDiscountorBlockConfigType
  index: number
  uiConfig: CatalogueUIConfigType
  blockTitle?: string
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
  selectionType?: 'QUANTITY' | 'PRICE'
  includeBundleProducts?: boolean
}

const QuantityDistributionBlock: React.FC<QuantityDistributionBlockProps> = ({
  blocksData,
  setFormState,
  catalogueApplicationType,
  catalogueCatagory,
  blockData,
  index,
  uiConfig,
  blockTitle,
  formErrors,
  setFormErrors,
  selectionType = 'QUANTITY',
  includeBundleProducts = false
}) => {
  const [isCatalogueSearchOpen, setIsCatalogueSearchOpen] = useState<boolean>(false)

  const handleBlockDelete = (index: number) => {
    const modifiedBlocksState = [...(blocksData ?? [])]
    modifiedBlocksState.splice(index, 1)

    switch (catalogueApplicationType) {
      case 'ENTITLED':
        setFormState(
          produce((draft: any) => {
            draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = modifiedBlocksState
          })
        )
        break
      case 'PREREQUISITE':
        setFormState(
          produce((draft: any) => {
            draft.conditions.prerequisitesConfig.quantityDistributorConfig = modifiedBlocksState
          })
        )
        break
    }
  }

  const saveBlockDataInFormState = (
    listItem: CatalogueDataType[] | null | undefined,
    blockData: QuantityDiscountorBlockConfigType,
    index: number,
    setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
  ) => {
    const filteredIndexes = formErrors?.quantityDistributorProduct?.indexes?.filter((item: number) => item !== index)
    if (listItem?.length ?? 0 > 0) {
      setFormErrors((oldValues: DiscountFormErrorState) => ({
        ...oldValues,
        quantityDistributorProduct: { status: true, errorCode: 'DC_FORM_008', indexes: filteredIndexes }
      }))
    }
    const modifiedBlockItemsConfig = { ...(blockData?.itemsConfig ?? {}) }
    switch (catalogueCatagory) {
      case 'PRODUCT_AND_VARIANT':
        modifiedBlockItemsConfig.items = getCatalogueIds(listItem)
        modifiedBlockItemsConfig.variants =
          listItem?.length === 1 && listItem?.[0].id === 'shopflo-all-products'
            ? ['shopflo-all-products']
            : getCatalogueIds(listItem, true)
        break
      case 'VARIANT':
        modifiedBlockItemsConfig.variants = listItem?.map((item: CatalogueDataType) => item?.id)
        modifiedBlockItemsConfig.items = []
        break
      default:
        modifiedBlockItemsConfig.items = listItem?.map((item: CatalogueDataType) => item?.id)
        modifiedBlockItemsConfig.variants = []
        break
    }
    modifiedBlockItemsConfig.catalogueMetadata = listItem as any
    const modifiedBlockData = {
      ...blockData,
      itemsConfig: modifiedBlockItemsConfig
    }
    setFormState(
      produce((draft: any) => {
        switch (catalogueApplicationType) {
          case 'ENTITLED':
            draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig[index] = modifiedBlockData
            break
          case 'PREREQUISITE':
            draft.conditions.prerequisitesConfig.quantityDistributorConfig[index] = modifiedBlockData
            break
        }
      })
    )
  }

  const handleFocus = (index: number, indexes: number[]) => {
    const filteredIndexes = indexes?.filter((item: number) => item !== index)
    const quantityDistributorErrorState = { status: true, errorCode: 'DC_FORM_008', indexes: filteredIndexes }
    if (filteredIndexes?.length !== 0) {
      setFormErrors((oldValues: DiscountFormErrorState) => ({
        ...oldValues,
        ...{ quantityDistributor: quantityDistributorErrorState }
      }))
    } else {
      setFormErrors((oldValues: DiscountFormErrorState) => ({
        ...oldValues,
        quantityDistributor: { status: false, errorCode: '', indexes: [] }
      }))
    }
  }

  return (
    <div className='flex flex-col rounded-lg border-1 border-[#F0F0F0] overflow-hidden'>
      <div className='border-b-1 border-[#F0F0F0] flex justify-between p-2 bg-[#F5F5F5]'>
        {catalogueApplicationType === 'ENTITLED' ? (
          <>
            {Boolean(blockData?.quantity !== null) ? (
              <>
                <div className='flex gap-[6px] items-center'>
                  <span className='text-[#949494] text-xs uppercase'>{'ANY'}</span>
                  <InputTextField
                    customClass='bg-[#FFF] rounded-lg'
                    textFieldId={`${index}_quantity_input`}
                    inputValue={blockData?.quantity}
                    inputProps={{ min: 1, max: 999999 }}
                    handleKeyDown={disablePlusMinusEKeys}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const value = Boolean(event.target.value) && getValueInRange(event.target.value, 999999, 1, true)
                      const modifiedBlockData = { ...blockData, quantity: value }
                      setFormState(
                        produce((draft: any) => {
                          draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig[index] =
                            modifiedBlockData
                        })
                      )
                    }}
                    inputType={'number'}
                    inputModeType={'tel'}
                    errorStatus={formErrors?.quantityDistributor?.indexes?.includes(index)}
                    handleFocus={() => handleFocus(index, formErrors?.quantityDistributor?.indexes)}
                  />
                  <span className='text-[#949494] text-xs uppercase whitespace-nowrap'>
                    {Boolean((blockData?.itemsConfig?.catalogueMetadata?.length ?? 0) > 1)
                      ? `${catalogueCatagory === 'COLLECTION' ? 'PRODUCTS' : ''} FROM`
                      : 'OF'}
                  </span>
                </div>
                <div className='flex gap-[6px] items-center'>
                  <span className='text-xs text-[#949494]'>{`${'DISCOUNTED ITEM'} ${index + 1}`}</span>
                  {Boolean(index !== 0) ? (
                    <XCircle
                      className='stroke-[2.5px] text-gray-dark w-[14px] h-[14px] cursor-pointer'
                      onClick={() => handleBlockDelete(index)}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </>
            ) : (
              <div className='flex gap-[6px] items-center'>
                <span className='text-[#949494] text-xs uppercase'>
                  {Boolean(blockTitle) ? blockTitle : 'DISCOUNTED ITEMS'}
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className='flex flex-col w-full space-y-2'>
              <div className='flex justify-between'>
                <div className='flex flex-row gap-[6px] items-center'>
                  <span className='text-[#949494] text-xs uppercase'>{'FROM'}</span>
                  <InputTextField
                    inputClass='bg-white'
                    textFieldId='min-threshold-input'
                    inputValue={selectionType === 'QUANTITY' ? blockData?.quantity : blockData?.price}
                    inputProps={{ min: 0, max: 999999 }}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const value = Boolean(event.target.value) && getValueInRange(event.target.value, 999999, 1, true)
                      const modifiedBlockData =
                        selectionType === 'QUANTITY'
                          ? { ...blockData, quantity: value }
                          : { ...blockData, price: value }
                      setFormState(
                        produce((draft: any) => {
                          draft.conditions.prerequisitesConfig.quantityDistributorConfig[index] = modifiedBlockData
                        })
                      )
                    }}
                    startIcon={selectionType === 'PRICE' && <span className='pl-1'>₹</span>}
                    inputType={'number'}
                    inputModeType={'tel'}
                    errorStatus={formErrors?.quantityDistributor?.indexes?.includes(index)}
                    errorMessage={errorMappings?.[formErrors?.minRequirement?.errorCode as keyof typeof errorMappings]}
                    handleFocus={() => handleFocus(index, formErrors?.quantityDistributor?.indexes)}
                  />
                  {blockData?.maxEnabled && (
                    <>
                      <span className='text-[#949494] text-xs uppercase'>{'TO'}</span>
                      <InputTextField
                        customClass='bg-[#FFF] rounded-lg w-20'
                        textFieldId='max-threshold-input'
                        inputValue={selectionType === 'QUANTITY' ? blockData?.maxQuantity : blockData?.maxPrice}
                        inputProps={{ min: 0, max: 999999 }}
                        startIcon={selectionType === 'PRICE' && <span className='pl-1'>₹</span>}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const value =
                            Boolean(event.target.value) && getValueInRange(event.target.value, 999999, 1, true)
                          const modifiedBlockData =
                            selectionType === 'QUANTITY'
                              ? { ...blockData, maxQuantity: value }
                              : { ...blockData, maxPrice: value }
                          setFormState(
                            produce((draft: any) => {
                              draft.conditions.prerequisitesConfig.quantityDistributorConfig[index] = modifiedBlockData
                            })
                          )
                        }}
                        inputType={'number'}
                        inputModeType={'tel'}
                        errorStatus={formErrors?.quantityDistributor?.indexes?.includes(index)}
                        errorMessage={
                          errorMappings?.[formErrors?.minRequirement?.errorCode as keyof typeof errorMappings]
                        }
                        handleFocus={() => handleFocus(index, formErrors?.quantityDistributor?.indexes)}
                      />
                    </>
                  )}
                  <span className='text-[#949494] text-xs uppercase whitespace-nowrap'>
                    {Boolean((blockData?.itemsConfig?.catalogueMetadata?.length ?? 0) > 1)
                      ? `${catalogueCatagory === 'COLLECTION' ? 'PRODUCTS' : ''} FROM`
                      : 'OF'}
                  </span>
                </div>
                <div className='flex gap-[6px] items-center'>
                  <span className='text-xs text-[#949494]'>{`${'ITEM CONDITION'} ${index + 1}`}</span>
                  {Boolean(index !== 0) ? (
                    <XCircle
                      className='stroke-[2.5px] text-gray-dark w-[14px] h-[14px] cursor-pointer'
                      onClick={() => handleBlockDelete(index)}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <FloCheckbox
                value={Boolean(blockData?.maxEnabled)}
                label={`Set a maximum ${selectionType === 'QUANTITY' ? 'quantity' : 'price'}`}
                setValue={(checked: boolean) => {
                  const modifiedBlockData = { ...blockData, maxEnabled: checked }
                  setFormState(
                    produce((draft: any) => {
                      draft.conditions.prerequisitesConfig.quantityDistributorConfig[index] = modifiedBlockData
                    })
                  )
                }}
              />
            </div>
          </>
        )}
      </div>
      {formErrors.quantityDistributor &&
        Array.isArray(formErrors.quantityDistributor.indexes) &&
        formErrors.quantityDistributor.indexes.includes(index) && (
          <span className='text-[13px] text-red-dark  bg-[#F5F5F5] pl-2 pb-2 font-medium'>
            {errorMappings?.[formErrors?.quantityDistributor?.errorCode as keyof typeof errorMappings]}
          </span>
        )}
      {formErrors.quantityDistributorProduct &&
        Array.isArray(formErrors.quantityDistributorProduct.indexes) &&
        formErrors.quantityDistributorProduct.indexes.includes(index) && (
          <span className='text-[13px] text-red-dark bg-[#F5F5F5] pl-2 pb-2 font-medium'>
            {errorMappings?.[formErrors?.quantityDistributorProduct?.errorCode as keyof typeof errorMappings]}
          </span>
        )}
      <div
        className='flex flex-col gap-3 pt-3 px-2'
        id={`${blockTitle?.toLowerCase().replace(/ /g, '-')}-search-container`}
      >
        <SearchAsYouType
          hideBrowseButton
          handleClick={setIsCatalogueSearchOpen}
          placeholder={uiConfig?.searchLabel ?? ''}
        />
        <div className='flex flex-col gap-3'>
          {Boolean(isCatalogueSearchOpen) ? (
            <CatalogueSearch
              isOpen={isCatalogueSearchOpen}
              setIsOpen={setIsCatalogueSearchOpen}
              catalogueCatagory={catalogueCatagory}
              searchedItemType={uiConfig?.valueType}
              selectedData={blockData?.itemsConfig?.catalogueMetadata}
              saveCatalogueData={(listItem: CatalogueDataType[] | null | undefined) =>
                saveBlockDataInFormState(listItem, blockData, index, setFormErrors)
              }
              showAllItemsOption={true}
              includeBundleProducts={includeBundleProducts}
            />
          ) : (
            <></>
          )}
          <div className={`${!Boolean(blockData?.itemsConfig?.catalogueMetadata?.length) ? 'hidden' : 'mb-2'}`}>
            <SelectedItemsList
              hideBorder={true}
              data={blockData?.itemsConfig?.catalogueMetadata}
              saveStateObject={(listItem: CatalogueDataType[] | null | undefined) =>
                saveBlockDataInFormState(listItem, blockData, index, setFormErrors)
              }
              catalogueCatagory={catalogueCatagory}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

interface QuantityDistributorProps {
  blocksData: QuantityDiscountorBlockConfigType[] | undefined
  catalogueCatagory: CatalogueCategoryType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  uiConfig: CatalogueUIConfigType
  catalogueApplicationType: CatalogueApplicationType
  blockTitle?: string
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
  selectionType?: 'QUANTITY' | 'PRICE'
  hideAddButton?: boolean
  id?: string
  includeBundleProducts?: boolean
}

const QuantityDistributor: React.FC<QuantityDistributorProps> = ({
  blocksData,
  catalogueCatagory,
  setFormState,
  uiConfig,
  catalogueApplicationType,
  blockTitle,
  formErrors,
  setFormErrors,
  selectionType = 'QUANTITY',
  hideAddButton,
  id,
  includeBundleProducts = false
}) => {
  const handleAddNewBlock = () => {
    const modifiedBlocksState = [
      ...(blocksData ?? []),
      { ...(selectionType === 'PRICE' ? { price: 100 } : { quantity: 1 }), itemsConfig: {} }
    ]

    switch (catalogueApplicationType) {
      case 'ENTITLED':
        setFormState(
          produce((draft: any) => {
            draft.setup.applicationConfig.entitlementsConfig.quantityDistributorConfig = modifiedBlocksState
          })
        )
        break
      case 'PREREQUISITE':
        setFormState(
          produce((draft: any) => {
            draft.conditions.prerequisitesConfig.quantityDistributorConfig = modifiedBlocksState
          })
        )
        break
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      {blocksData?.map((blockData: QuantityDiscountorBlockConfigType, index: number) => {
        return (
          <div key={`${index}`} className='gap-3 flex flex-col' id={id}>
            <QuantityDistributionBlock
              blocksData={blocksData}
              setFormState={setFormState}
              catalogueApplicationType={catalogueApplicationType}
              catalogueCatagory={catalogueCatagory}
              blockData={blockData}
              index={index}
              uiConfig={uiConfig}
              blockTitle={blockTitle}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              selectionType={selectionType}
              includeBundleProducts={includeBundleProducts}
            />
            {!hideAddButton ? (
              <>
                <div
                  className={`border-t-[0.5px] mt-3 mb-2 h-0 -ml-4 -mr-4  ${
                    index === blocksData.length - 1 ? 'hidden' : ''
                  }`}
                >
                  <span className='relative rounded-lg bg-[#FFF] text-[#949494] text-xs -top-[13px] left-4 px-1'>
                    AND
                  </span>
                </div>
                <span
                  className={`bg-[#f0f0f0] rounded-lg text-[#949494] px-1 text-sm cursor-pointer shadow w-fit ${
                    index !== blocksData.length - 1 ? 'hidden' : ''
                  }`}
                  onClick={() => handleAddNewBlock()}
                >
                  + AND
                </span>
              </>
            ) : (
              <></>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default QuantityDistributor
